import { createContext, useContext, useState, useCallback } from 'react';

const AdminStoreContext = createContext(null);

export const useAdminStore = () => {
  const context = useContext(AdminStoreContext);
  if (!context) {
    throw new Error('useAdminStore must be used within AdminStoreProvider');
  }
  return context;
};

export const AdminStoreProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    userSearch: '',
    userStatus: 'all',
    logType: 'all',
    logStartDate: null,
    logEndDate: null,
  });

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const updateUserInList = useCallback((userId, userData) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...userData } : u)));
  }, []);

  const addUser = useCallback((user) => {
    setUsers((prev) => [...prev, user]);
  }, []);

  const removeUser = useCallback((userId) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const addLog = useCallback((log) => {
    setLogs((prev) => [log, ...prev]);
  }, []);

  const reset = useCallback(() => {
    setUsers([]);
    setSelectedUser(null);
    setLogs([]);
    setRoles([]);
    setSystemSettings(null);
    setLoading(false);
    setError(null);
    setFilters({
      userSearch: '',
      userStatus: 'all',
      logType: 'all',
      logStartDate: null,
      logEndDate: null,
    });
  }, []);

  const value = {
    // State
    users,
    selectedUser,
    logs,
    roles,
    systemSettings,
    loading,
    error,
    filters,
    // Actions
    setLoading,
    setError,
    setUsers,
    setSelectedUser,
    setLogs,
    setRoles,
    setSystemSettings,
    setFilters: updateFilters,
    updateUserInList,
    addUser,
    removeUser,
    addLog,
    reset,
  };

  return <AdminStoreContext.Provider value={value}>{children}</AdminStoreContext.Provider>;
};

export default useAdminStore;
